import * as Sentry from '@sentry/react';
import { API } from 'utils';
import { filterAddressesWithoutNulls } from 'utils/getAddressesWithoutNulls';
import { RootDispatch } from '../store';
import {
  AddressCreateDTO,
  AddressDTO,
  AddressEditDTO,
  ClientResponse,
  EmailCreateDTO,
  EmailDTO,
  EmailEditDTO,
  PhoneCreateDTO,
  PhoneDTO,
  PhoneEditDTO
} from './client.model';
import { mapClientToDeprecated } from '../user/user.mapper';
import { Client } from '../user/user.model';

const endpoint = '/client';
const clientEffects = (dispatch: RootDispatch) => ({
  async fetchEmails(): Promise<EmailDTO[] | undefined> {
    const url = `${endpoint}/emails`;

    try {
      const response = await API.get<void, ClientResponse<EmailDTO[]>>(url);
      if (response) {
        dispatch.client.storeEmails(response.data);
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching emails:', error);
      return undefined;
    }
  },
  async fetchEmail(emailAddressId: string): Promise<EmailDTO | undefined> {
    const url = `${endpoint}/emails/${emailAddressId}`;

    try {
      const response = await API.get<void, ClientResponse<EmailDTO>>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching emails:', error);
      return undefined;
    }
  },
  async editEmail({
    id,
    ...rest
  }: EmailEditDTO): Promise<ClientResponse<EmailDTO[]> | undefined> {
    const url = `${endpoint}/emails/${id}`;

    try {
      return await API.put<EmailCreateDTO, ClientResponse<EmailDTO[]>>(
        url,
        rest
      );
    } catch (error) {
      console.error('Error fetching emails:', error);
      return undefined;
    }
  },
  async addEmail(
    email: EmailCreateDTO
  ): Promise<ClientResponse<EmailDTO[]> | undefined> {
    const url = `${endpoint}/emails`;

    try {
      return await API.post<EmailCreateDTO, ClientResponse<EmailDTO[]>>(
        url,
        email
      );
    } catch (error) {
      console.error('Error fetching emails:', error);
      return undefined;
    }
  },
  async deleteEmail(
    emailAddressId: string
  ): Promise<ClientResponse<EmailDTO[]> | undefined> {
    const url = `${endpoint}/emails/${emailAddressId}`;
    try {
      return await API.delete<void, ClientResponse<EmailDTO[]>>(url);
    } catch (error) {
      console.error('Error fetching emails:', error);
      return undefined;
    }
  },
  async fetchPhones(): Promise<ClientResponse<PhoneDTO[]> | undefined> {
    const url = `${endpoint}/phones`;
    try {
      const response = await API.get<void, ClientResponse<PhoneDTO[]>>(url);
      if (response) {
        dispatch.client.storePhones(response.data);
      }
      return response;
    } catch {
      return undefined;
    }
  },
  async fetchPhone(
    phoneNumberId: string
  ): Promise<ClientResponse<PhoneDTO> | undefined> {
    const url = `${endpoint}/phones/${phoneNumberId}`;

    try {
      return await API.get<void, ClientResponse<PhoneDTO>>(url);
    } catch (error) {
      console.error('Error fetching phone:', error);
      return undefined;
    }
  },
  async editPhone({
    id,
    ...rest
  }: PhoneEditDTO): Promise<ClientResponse<PhoneDTO> | undefined> {
    const url = `${endpoint}/phones/${id}`;

    try {
      return await API.put<PhoneCreateDTO, ClientResponse<PhoneDTO>>(url, rest);
    } catch (error) {
      console.error('Error editing phone:', error);
      return undefined;
    }
  },
  async addPhone(
    phone: PhoneCreateDTO
  ): Promise<ClientResponse<PhoneDTO> | undefined> {
    const url = `${endpoint}/phones`;

    try {
      return await API.post<PhoneCreateDTO, ClientResponse<PhoneDTO>>(
        url,
        phone
      );
    } catch (error) {
      console.error('Error adding phone:', error);
      return undefined;
    }
  },
  async deletePhone(
    phoneNumberId: string
  ): Promise<ClientResponse<PhoneDTO> | undefined> {
    const url = `${endpoint}/phones/${phoneNumberId}`;

    try {
      return await API.delete<void, ClientResponse<PhoneDTO>>(url);
    } catch (error) {
      console.error('Error deleting phone:', error);
      return undefined;
    }
  },
  async fetchAddresses(): Promise<AddressDTO[] | undefined> {
    const url = `${endpoint}/addresses`;

    try {
      const response = await API.get<void, ClientResponse<AddressDTO[]>>(url);
      const filteredAddresses = filterAddressesWithoutNulls(response.data);
      if (response) {
        dispatch.client.storeAddresses(filteredAddresses);
      }
      return filteredAddresses;
    } catch {
      return undefined;
    }
  },
  async fetchAddress(addressId: string): Promise<AddressDTO | undefined> {
    const url = `${endpoint}/addresses/${addressId}`;

    try {
      const response = await API.get<void, ClientResponse<AddressDTO>>(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching address:', error);
      return undefined;
    }
  },
  async editAddress({
    id,
    ...rest
  }: AddressEditDTO): Promise<ClientResponse<AddressDTO> | undefined> {
    const url = `${endpoint}/addresses/${id}`;

    try {
      return await API.put<AddressCreateDTO, ClientResponse<AddressDTO>>(
        url,
        rest
      );
    } catch (error) {
      console.error('Error editing address:', error);
      return undefined;
    }
  },
  async addAddress(
    address: AddressCreateDTO
  ): Promise<ClientResponse<AddressDTO> | undefined> {
    const url = `${endpoint}/addresses`;

    try {
      return await API.post<AddressCreateDTO, ClientResponse<AddressDTO>>(
        url,
        address
      );
    } catch (error) {
      console.error('Error adding address:', error);
      return undefined;
    }
  },
  async deleteAddress(
    addressId: string
  ): Promise<{ success: boolean } | undefined> {
    const url = `${endpoint}/addresses/${addressId}`;

    try {
      return await API.delete<void, { success: boolean }>(url);
    } catch (error) {
      console.error('Error deleting address:', error);
      return undefined;
    }
  },
  async fetchClientData(): Promise<void> {
    const url = `/client`;
    API.get<undefined, ClientResponse<Client>>(url)
      .then((payload) => {
        const { data } = payload;
        const user = mapClientToDeprecated(data);
        dispatch.user.changeUserData({ data: user });
        if (user.id) {
          Sentry.setUser({ id: user.id.toString() });
        }
      })
      .catch((error) => {
        console.error('Error fetching client data:', error);
        return undefined;
      });
  }
});

export default clientEffects;
